import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, ShieldCheck, Eye, MessageCircle } from "lucide-react";
import { api, rp } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Textarea } from "@/components/ui/textarea";

const TABS = [
  ["submitted", "Menunggu Verifikasi"],
  ["reupload_requested", "Minta Unggah Ulang"],
  ["accepted", "Diterima"],
  ["rejected", "Ditolak"],
  ["all", "Semua"],
];

const BADGE = {
  submitted: "bg-amber-100 text-amber-800 border-amber-200",
  accepted: "bg-emerald-100 text-emerald-800 border-emerald-200",
  rejected: "bg-red-100 text-red-800 border-red-200",
  reupload_requested: "bg-sky-100 text-sky-800 border-sky-200",
};

// Status bukti → kejadian notifikasi customer
const NOTICE_EVENT = {
  accepted: "payment_accepted",
  rejected: "payment_rejected",
  reupload_requested: "payment_reupload",
};

const EMAIL_NOTE = {
  queued: "Email otomatis ke customer sudah dikirim.",
  no_email: "Customer belum mengisi email — kirim lewat WhatsApp saja.",
  test_data: "Data uji — email sungguhan tidak dikirim.",
  duplicate: "Email untuk kejadian ini sudah pernah dikirim.",
  error: "Email gagal disiapkan — kirim lewat WhatsApp.",
};

export default function PaymentProofs() {
  const { user } = useAuth();
  const [status, setStatus] = useState("submitted");
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState("");
  const [reason, setReason] = useState({});
  const [preview, setPreview] = useState(null);
  const canVerify = ["owner", "finance"].includes(user?.role)
    || (user?.perms || []).includes("payments:write");

  const load = useCallback(() => {
    api.get("/payment-proofs", { params: { status } })
      .then((r) => setRows(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Gagal memuat antrean"));
  }, [status]);
  useEffect(() => { load(); }, [load]);

  const act = async (row, action) => {
    const why = (reason[row.id] || "").trim();
    if (action !== "accept" && why.length < 5) {
      return toast.error("Tulis alasannya minimal 5 karakter supaya customer tahu perbaikannya");
    }
    setBusy(row.id + action);
    try {
      const r = await api.post(`/payment-proofs/${row.id}/verify`, { action, reason: why });
      toast.success(`Status pembayaran: ${r.data.payment_status}`);
      const notice = r.data.customer_notice;
      if (notice) toast.message(EMAIL_NOTE[notice.email_status] || "Notifikasi customer disiapkan");
      setReason((p) => ({ ...p, [row.id]: "" }));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memverifikasi");
    } finally { setBusy(""); }
  };

  const sendWa = async (row) => {
    const event = NOTICE_EVENT[row.proof?.status];
    if (!event) return;
    try {
      const r = await api.get(`/customer-messages/${row.id}/wa`, { params: { event } });
      if (!r.data.wa_url) return toast.error("Nomor WhatsApp customer tidak valid");
      window.open(r.data.wa_url, "_blank");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal membuat template");
    }
  };

  const openProof = async (row) => {
    try {
      const r = await api.get(`/payment-proofs/${row.id}/file`, { responseType: "blob" });
      setPreview({ url: URL.createObjectURL(r.data), row, type: r.data.type });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bukti tidak bisa dibuka");
    }
  };

  return (
    <div data-testid="admin-payment-proofs">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl tracking-tight">Verifikasi Bukti Transfer</h1>
          <p className="mt-1 text-sm text-ink-soft max-w-2xl">
            Chat WhatsApp bukan bukti pembayaran. Booking baru menjadi <b>Paid / Confirmed</b> setelah
            Owner atau Finance menekan <b>Terima</b> di sini.
          </p>
        </div>
        <button data-testid="expire-holds-btn"
          onClick={() => api.post("/bookings/expire-holds").then((r) => {
            toast.success(`${r.data.count} hold kedaluwarsa dilepas`); load();
          }).catch((e) => toast.error(e?.response?.data?.detail || "Gagal"))}
          className="text-xs px-4 py-2.5 rounded-md border border-black/15 hover:border-lm-red">
          Lepas hold kedaluwarsa
        </button>
      </div>

      <div className="mt-5 flex flex-wrap gap-2">
        {TABS.map(([k, label]) => (
          <button key={k} data-testid={`proof-tab-${k}`} onClick={() => setStatus(k)}
            className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${status === k ? "bg-lm-red text-white border-lm-red" : "border-black/15 hover:border-lm-red/50"}`}>
            {label}
          </button>
        ))}
      </div>

      {rows.length === 0 && (
        <div data-testid="proofs-empty" className="mt-6 rounded-lg border border-dashed border-black/15 p-10 text-center text-sm text-ink-soft">
          Belum ada bukti transfer pada status ini.
        </div>
      )}

      <div className="mt-5 grid gap-4 lg:grid-cols-2">
        {rows.map((r) => (
          <div key={r.id} data-testid={`proof-card-${r.id}`} className="bg-white border border-black/10 rounded-lg p-5">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="font-semibold" data-testid={`proof-code-${r.id}`}>{r.booking_code}</div>
                <div className="text-xs text-ink-soft mt-0.5">
                  {r.customer_name} · {r.branch_name || "-"} · {r.rental_date}
                </div>
                <div className="text-xs text-ink-soft">{r.package} × {r.quantity} meja</div>
              </div>
              <span className={`text-[11px] px-2 py-1 rounded-full border ${BADGE[r.proof?.status] || "bg-neutral-100 border-neutral-200"}`}>
                {r.proof?.status}
              </span>
            </div>

            <div className="mt-4 space-y-1 text-sm">
              <Row l="Sewa" v={rp(r.final_rental_total || 0)} />
              <Row l="Deposit" v={rp(r.deposit_amount || 0)} />
              <Row l="Total transfer" v={<span className="font-semibold text-lm-red" data-testid={`proof-total-${r.id}`}>{rp(r.total_payment || 0)}</span>} />
              <Row l="Status bayar" v={r.payment_status} />
              <Row l="Rekening refund" v={`${r.refund_bank_name || "-"} · ${r.refund_account_number || r.refund_account_number_masked || "-"} · ${r.refund_account_holder_name || "-"}`} />
            </div>

            <div className="mt-4 flex flex-wrap gap-2 items-center">
              {r.proof?.status && r.proof.status !== "none" && canVerify && (
                <button data-testid={`view-proof-${r.id}`} onClick={() => openProof(r)}
                  className="text-xs inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-black/15 hover:border-lm-red">
                  <Eye size={13} /> Lihat bukti
                </button>
              )}
              <span className="text-xs text-ink-soft">{r.proof?.file_name} · {(r.proof?.uploaded_at || "").slice(0, 16).replace("T", " ")}</span>
            </div>
            {r.proof?.reject_reason && (
              <p className="mt-2 text-xs text-red-700" data-testid={`proof-reason-${r.id}`}>Alasan: {r.proof.reject_reason}</p>
            )}

            {canVerify && NOTICE_EVENT[r.proof?.status] && (
              <div className="mt-4 border-t border-black/10 pt-4">
                <button data-testid={`wa-confirm-${r.id}`} onClick={() => sendWa(r)}
                  className="text-xs inline-flex items-center gap-1.5 px-4 py-2.5 rounded-md border border-emerald-300 text-emerald-800 hover:bg-emerald-50">
                  <MessageCircle size={13} /> Kirim WhatsApp ke customer
                </button>
                <p className="mt-2 text-[11px] text-ink-soft" data-testid={`notice-status-${r.id}`}>
                  {r.proof?.status === "accepted"
                    ? "Pesan sudah terisi: status bayar, deposit, info Hub, alamat, dan titik Maps pengembalian."
                    : "Pesan sudah terisi: alasan dan langkah selanjutnya untuk customer."}
                  {" "}
                  {r.last_customer_notice?.email_status
                    ? (EMAIL_NOTE[r.last_customer_notice.email_status] || "")
                    : ""}
                </p>
              </div>
            )}

            {canVerify && ["submitted"].includes(r.proof?.status) && (
              <div className="mt-4 border-t border-black/10 pt-4">
                <Textarea data-testid={`proof-reason-input-${r.id}`} rows={2}
                  placeholder="Alasan (wajib untuk Tolak / Minta unggah ulang)"
                  value={reason[r.id] || ""} onChange={(e) => setReason((p) => ({ ...p, [r.id]: e.target.value }))} />
                <div className="mt-3 flex flex-wrap gap-2">
                  <button data-testid={`accept-${r.id}`} disabled={!!busy} onClick={() => act(r, "accept")}
                    className="lm-btn-primary rounded-md px-4 py-2.5 text-xs font-semibold inline-flex items-center gap-1.5 disabled:opacity-40">
                    {busy === r.id + "accept" ? <Loader2 size={13} className="animate-spin" /> : <ShieldCheck size={13} />} Terima — tandai Paid
                  </button>
                  <button data-testid={`reupload-${r.id}`} disabled={!!busy} onClick={() => act(r, "reupload")}
                    className="px-4 py-2.5 rounded-md border border-black/15 text-xs hover:border-lm-red disabled:opacity-40">
                    Minta unggah ulang
                  </button>
                  <button data-testid={`reject-${r.id}`} disabled={!!busy} onClick={() => act(r, "reject")}
                    className="px-4 py-2.5 rounded-md border border-red-300 text-red-700 text-xs hover:bg-red-50 disabled:opacity-40">
                    Tolak &amp; batalkan
                  </button>
                </div>
              </div>
            )}
            {!canVerify && (
              <p className="mt-3 text-xs text-ink-soft">Hanya Owner dan Finance yang boleh membuka berkas dan memverifikasi.</p>
            )}
          </div>
        ))}
      </div>

      {preview && (
        <div data-testid="proof-preview" onClick={() => setPreview(null)}
          className="fixed inset-0 z-50 bg-black/70 p-6 flex items-center justify-center">
          <div className="bg-white rounded-lg p-4 max-w-2xl w-full max-h-[85vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between gap-4">
              <div className="text-sm font-semibold">{preview.row.booking_code}</div>
              <button data-testid="close-preview" onClick={() => setPreview(null)} className="text-xs text-ink-soft">Tutup</button>
            </div>
            {String(preview.type).includes("pdf")
              ? <iframe title="bukti" src={preview.url} className="mt-3 w-full h-[70vh]" />
              : <img alt="bukti transfer" src={preview.url} className="mt-3 w-full object-contain" />}
          </div>
        </div>
      )}
    </div>
  );
}

const Row = ({ l, v }) => (
  <div className="flex justify-between gap-4"><span className="text-ink-soft">{l}</span><span className="text-right">{v}</span></div>
);
